import { Skeleton } from 'antd';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useParams } from 'react-router-dom';
import { apiGetEventNoAuth } from '../../../../api/events';
import logo from "../../../../assets/images/logo.png";
import logoWhite from "../../../../assets/images/logo-white.png";
import { ReactComponent as Drecrement } from "../../../../assets/images/ticket/decrement.svg";
import { ReactComponent as Increment } from "../../../../assets/images/ticket/increment.svg";
import { ReactComponent as Location } from "../../../../assets/images/ticket/location.svg";
import { ReactComponent as Transfer } from "../../../../assets/images/ticket/transfer.svg";
import ShareModal from "../ShareModal/ShareModal";
import './TicketSelector.scss';
import {EVENT_DIGITAL_ADDRESS_PLATFORM_CHOICES} from "../../../../utils/eventsSelect";
import { sanitizeHtml } from "../../../../helpers/sanitizeHtml";

const TicketSelector = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState({});
  const [formattedStartDate, setFormattedStartDate] = useState('');
  const [formattedEndDate, setFormattedEndDate] = useState('');
  const [quantities, setQuantities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const getMapUrl = () => {
    const { name, street, number, city, state } = event?.address || {};
    const address = `${name}, ${street}, ${number}, ${city}, ${state}`;
    return `https://www.google.com/maps/search/${encodeURIComponent(address)}`;
  };

  useEffect(() => {
    apiGetEventNoAuth(id).then(data => {
      setEvent(data);
      setQuantities(data?.tickets?.map(() => 0));
      setIsLoading(false);
    }).catch(error => {
      if (error.message.includes('400')) {
        navigate('/evento-nao-encontrado');
      } else {
        setIsLoading(false);
      }
    });
  }, [id, navigate]);

  useEffect(() => {
    if (event && event.starts_at && event.ends_at) {
      try {
        const startDate = parseISO(event.starts_at);
        const endDate = parseISO(event.ends_at);

        const formattedStart = format(startDate, "dd MMM - yyyy • HH:mm", { locale: ptBR });
        const formattedEnd = format(endDate, "dd MMM - yyyy • HH:mm", { locale: ptBR });

        setFormattedStartDate(formattedStart);
        setFormattedEndDate(formattedEnd);
      } catch (error) {
        console.error("Error parsing or formatting date:", error);
      }
    }
  }, [event]);

  function renderHTML(html) {
    return (
      <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(html) }} />
    );
  }

  const calculateTotal = () => {
    return event?.tickets?.reduce((total, ticket, index) => {
      return total + (ticket.amount * quantities[index]);
    }, 0) / 100;
  };

  const hasItems = quantities.some(quantity => quantity > 0);

  const updateQuantity = (index, change) => {
    setQuantities(prevQuantities => {
      const newQuantities = [...prevQuantities];
      const newQuantity = Math.max(0, newQuantities[index] + change);
      newQuantities[index] = newQuantity;
      return newQuantities;
    });
  };

  const total = calculateTotal();
  const maxInstallments = parseInt(event?.max_installments) || 12;
  const installment = (total / maxInstallments).toFixed(2);

  const getTicketDisplayName = (plural) => {
    switch (event?.nomenclature) {
      case "CONTRIBUTION":
        if (plural) {
          return "Contribuições";
        }
        return "Contribuição";
      case "TICKETING":
        if (plural) {
          return "Ingressos";
        }
        return "Ingresso";
      case "REGISTRATION":
        if (plural) {
          return "Inscrições";
        }
        return "Inscrição";
      default:
        if (plural) {
          return "Ingressos";
        }
        return "Ingresso";
    }
  }

  const handleCheckout = () => {
    const selectedTickets = event?.tickets?.map((ticket, index) => ({
      id: ticket.id,
      name: ticket.name,
      quantity: quantities[index],
      price: ticket.amount / 100,
      questions: ticket.questions
    })).filter(ticket => ticket.quantity > 0);

    navigate(`/evento/checkout/${event.id}`, { state: { tickets: selectedTickets, total, event } });
  };

  const getPlatformName = () => {
    if (event?.digital_address?.platform === "OTHER") {
      let name = event?.digital_address?.other_platform;
      return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
    }
    for (const platform of EVENT_DIGITAL_ADDRESS_PLATFORM_CHOICES) {
      if (platform.value === event?.digital_address?.platform) {
        return platform.label;
      }
    }
    return "Internet";
  }

  return (
    <>
      <Helmet>
        <title>{event.name}</title>
      </Helmet>
      <div className='TicketSelector'>
        <div style={{ width: '100%', height: '66px', backgroundColor: 'white', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <img src={logo} alt="Logo" style={{ height: '100%' }} />
        </div>
        <div className="header-event" style={{ backgroundImage: `url(${isLoading ? '' : event?.image_url})` }}>
          <div className="header-image-container">
            {!isLoading && <div className="header-image" style={{ backgroundImage: `url(${isLoading ? '' : event?.image_url})` }}>
              {!isLoading && <button onClick={openModal}>
                <Transfer />Compartilhar
              </button>}
            </div>}
            <div className="header-disabled">
              {!isLoading && <button onClick={openModal}>
                <Transfer />Compartilhar
              </button>}
            </div>
          </div>
        </div>
        <div className="event-section">
          {!isLoading && <div className="summary">
            <div className="info-summary">
              {maxInstallments > 1 && (
                <div className="tag">
                  <span className="ion--card-outline" /><p>Parcele sua compra em até {maxInstallments}x</p>
                </div>
              )}
              <h1>{event.name}</h1>
              <div className="event-information-container">
                <span className="mdi--clock-outline" />
                <div>
                  <p className="title">
                    {formattedStartDate} - {formattedEndDate}
                  </p>
                </div>
              </div>
              <div className="event-information-container">
                <span className="carbon--location" />
                <div>
                  {event?.type === 'IN_PERSON' &&
                    <p className="title">
                      {`${event?.address?.street}, ${event?.address?.number}${event?.address?.complement ? ` - ${event?.address?.complement}` : ''}, ${event?.address?.neighborhood}, ${event?.address?.city} - ${event?.address?.state}, ${event?.address?.postal_code}`}
                    </p>
                  }
                  {event?.type === 'ONLINE' &&
                    <p className="title">
                      Evento Online via {getPlatformName()}
                    </p>
                  }
                </div>
              </div>
            </div>

            <div className="ticket-section">
              <div className="header">
                <p>{getTicketDisplayName()}</p>
                <div className="price-info">
                  R$ {calculateTotal().toFixed(2)}
                </div>
              </div>
              {event?.tickets?.sort((a,b) => a.amount - b.amount).map((ticket, index) => {
                let expired = false;
                let toBeReleased = false;
                if (ticket.sales_period_type === "PERIOD") {
                  const salesStartsAt = new Date(ticket.sales_starts_at);
                  const salesEndsAt = new Date(ticket.sales_ends_at);
                  const now = new Date();
                  if (salesStartsAt > now) {
                    toBeReleased = true;
                  }else if (salesEndsAt < now) {
                    expired = true;
                  }
                }
                return (<div key={ticket.id} className="ticket-item">
                  <div className="ticket-details">
                    <div className="ticket-title">
                      <span>{ticket.name}</span>
                    </div>
                    <p className="price">
                      <span>
                        {ticket.amount === 0 ? 'Gratuito' : `R$ ${(ticket.amount / 100).toFixed(2)}`}
                      </span>
                      {ticket.amount > 0 && <span> {parseInt(event.max_installments_no_tax) > 0  ? null : "(+ taxa)"}</span>}
                    </p>
                    {ticket.amount > 0 && (
                      <>
                      {ticket.quantity_available > 0 && <>
                        {(!expired && !toBeReleased) && maxInstallments > 1 && <p className="installments">em até {maxInstallments}x R$ {((ticket.amount / maxInstallments) / 100).toFixed(2)} {parseInt(event.max_installments_no_tax) >= maxInstallments  ? null : "(+ taxa)"}</p>}
                            <p className="validity">
                              {toBeReleased && <span className="tobereleased">Vendas iniciam em { new Date(ticket.sales_starts_at).toLocaleDateString('pt-BR')}</span>}
                              {expired && <span className="expired">Vendas encerradas</span>}
                              {(!expired && !toBeReleased) && <>Vendas até {event ? ticket.sales_period_type === "PERIOD" ? new Date(ticket.sales_ends_at).toLocaleDateString('pt-BR') : new Date(event.ends_at).toLocaleDateString('pt-BR') : 'data não disponível'}</>}
                            </p>
                            {(ticket.quantity_available > 0 && (quantities[index] >= ticket.quantity_available || ticket.quantity_available < 5) && (!expired && !toBeReleased)) && <p className={"running-low"}>
                             Resta{ticket.quantity_available > 1 ? 'm' : ''} apenas {ticket.quantity_available} ingresso{ticket.quantity_available > 1 ? 's' : ''}
                            </p>}
                          </>
                      }
                      {ticket.quantity_available <= 0 && (
                        <p className="sold-out">Esgotado</p>
                      )}
                      </>
                    )}
                  </div>
                  <div className="quantity-controls">
                    {((!expired && !toBeReleased) && ticket.quantity_available > 0)&& <>
                      <button aria-label="Decrease Amount" onClick={() => updateQuantity(index, -1)} className={`decrease-btn ${quantities[index] === 0 ? 'disabled' : ''}`}><Drecrement /></button>
                      <span className="quantity">{quantities[index]}</span>
                      <button aria-label="Increase Amount" onClick={() => updateQuantity(index, quantities[index] >= ticket.quantity_available ? 0 : 1)} className={`increase-btn ${quantities[index] >= ticket.quantity_available ? 'disabled' : ''}`}><Increment /></button>
                    </>}
                  </div>
                </div>
              )})}
              {!hasItems && <div className="total-summary">
                <div className="purchase-button">
                  <button type="button">{
                    event?.nomenclature === 'REGISTRATION' ?
                      'SELECIONE A INSCRIÇÃO'
                      : event?.nomenclature === 'CONTRIBUTION' ?
                        'SELECIONE UMA CONTRIBUIÇÃO'
                        :
                        'SELECIONE UM INGRESSO'
                  }</button>
                </div>
              </div>}
              {hasItems && (
                <div className="total-summary">
                  <div className="total-details">
                    <p>Total</p>
                    <p className="total-price">R$ {total.toFixed(2)}</p>
                    {maxInstallments > 1 && <p className="total-installments">ou {maxInstallments}x R$ {installment}</p>}
                  </div>
                  <div className="open-button">
                    <button type="button" onClick={handleCheckout}>{
                      event?.nomenclature === 'REGISTRATION' ?
                        'FAZER INSCRIÇÃO'
                       : event?.nomenclature === 'CONTRIBUTION' ?
                        'CONTRIBUIR'
                      :
                        'COMPRAR INGRESSO'
                    }</button>
                  </div>
                </div>
              )}
            </div>
          </div>}
          {isLoading && <div className="summary">
            <Skeleton active paragraph={{ rows: 10 }} />
          </div>}
          {!isLoading && <div className="event-payment">
            <div className="ticket-description">
              <div className="description">
                <p className='title'>Descrição do evento</p>
                <div className="render-html">
                  {event?.description && renderHTML(JSON.parse(event?.description))}
                </div>
                <div className="divider" />
                { event?.type === 'ONLINE' &&
                  <p className='title'>Evento Online via {getPlatformName()}</p>
                }
                { event?.type === 'IN_PERSON' &&
                  <>
                    <p className='title'>Local</p>
                    <p className='address subtitle'>
                      {event?.address?.name}
                    </p>
                    <p className='address info'>
                      {event?.address?.street}, {event?.address?.number}
                      <br />
                      {event?.address?.city} - {event?.address?.state}
                    </p>
                    <a
                      className="location-button"
                      href={getMapUrl()}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Location />
                      VER NO MAPA
                    </a>
                  </>
                }

              </div>
            </div>
          </div>}
        </div>
        <ShareModal event={event} isOpen={isModalOpen} onClose={closeModal} />
        <div className="footer">
          <div className="footer-content">
            <img src={logoWhite} alt="Logo" style={{ height: '100%' }} />
            <a href="/termos-de-uso" target="_blank" rel="noopener noreferrer">Termos de Uso</a>
            <a href="/politica-de-privacidade" target="_blank" rel="noopener noreferrer">Política de Privacidade</a>
            <a href="/politica-de-cancelamento" target="_blank" rel="noopener noreferrer">Política de Pagamentos, Reembolsos e Cancelamentos</a>
          </div>
        </div>
      </div>
    </>
  );
}

export default TicketSelector;
